import { useState, useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BACKEND_URL } from "@/constants/api";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/layouts/PageHeader";
import {
  LucideTrash,
  LucidePlus,
  LucideBookOpen,
  LucidePencil,
  LucideCopy,
} from "lucide-react";
import ClassList from "@/components/entities/ClassList";
import { EmptyState } from "@/components/ui/EmptyState";
import { useSchool } from "@/context/SchoolContext";
import { CreateClassModal } from "@/components/modals/CreateClassModal";
import { EditClassModal } from "@/components/modals/EditClassModal";
import { DuplicateClassModal } from "@/components/modals/DuplicateClassModal";

export default function Classes() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { memberships } = useSchool();

  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [duplicatingClass, setDuplicatingClass] = useState(null);
  const [duplicating, setDuplicating] = useState(false);

  const fetchClasses = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/report-groups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch classes");
      setClasses(await res.json());
      setFetchError(null);
    } catch (err) {
      console.error("Error fetching classes:", err);
      setFetchError("Could not load your classes. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async (formData) => {
    setCreating(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/report-groups`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error("Failed to create class");
      const newClass = await res.json();
      setClasses((prev) => [...prev, newClass]);
      setShowModal(false);
      navigate(`/classes/${newClass._id}`);
    } catch (err) {
      console.error("Error creating class:", err);
      toast.error("Failed to create class. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (classItem) => {
    const classLabel =
      classItem.name ||
      [classItem.grade && `Grade ${classItem.grade}`, classItem.schoolYear]
        .filter(Boolean)
        .join(" · ") ||
      "this class";
    if (
      !confirm(
        `Delete "${classLabel}"? This will also delete all report cards in this class.`
      )
    )
      return;
    try {
      const token = await getToken();
      await fetch(`${BACKEND_URL}api/report-groups/${classItem._id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setClasses((prev) => prev.filter((c) => c._id !== classItem._id));
      toast.success("Class deleted.");
    } catch (err) {
      console.error("Error deleting class:", err);
      toast.error("Failed to delete class. Please try again.");
    }
  };

  const handleUpdate = async (classItem, editForm) => {
    setUpdating(true);
    try {
      const token = await getToken();
      const res = await fetch(
        `${BACKEND_URL}api/report-groups/${classItem._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(editForm),
        }
      );
      if (!res.ok) throw new Error("Failed to update class");
      const updated = await res.json();
      setClasses((prev) =>
        prev.map((c) => (c._id === updated._id ? updated : c))
      );
      setEditingClass(null);
      toast.success("Class updated.");
    } catch (err) {
      console.error("Error updating class:", err);
      toast.error("Failed to update class. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  const handleDuplicate = async (classItem, name) => {
    setDuplicating(true);
    try {
      const token = await getToken();
      const res = await fetch(
        `${BACKEND_URL}api/report-groups/${classItem._id}/duplicate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ name }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw Object.assign(
          new Error(body.message || "Failed to duplicate class"),
          { status: res.status }
        );
      }
      const newClass = await res.json();
      setClasses((prev) => [newClass, ...prev]);
      setDuplicatingClass(null);
      toast.success("Class duplicated successfully.");
    } catch (err) {
      console.error("Error duplicating class:", err);
      if (err.status === 403) {
        toast.error(err.message, { duration: 6000 });
      } else {
        toast.error("Failed to duplicate class. Please try again.");
      }
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <>
      <div className="px-10 py-9 max-w-[960px] mx-auto max-md:px-4 max-md:pt-5 max-md:pb-6">
        <PageHeader
          title="My Classes"
          subtitle="Each class holds a group of student report cards."
          action={
            <Button onClick={() => setShowModal(true)} className="gap-2">
              <LucidePlus size={15} />
              New Class
            </Button>
          }
        />

        {fetchError && (
          <div className="mb-4 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {fetchError}
          </div>
        )}

        {loading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-[60px] bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        ) : classes.length === 0 ? (
          <EmptyState
            icon={LucideBookOpen}
            title="No classes yet"
            description="Create your first class to start generating report cards."
            action={
              <Button
                onClick={() => setShowModal(true)}
                variant="outline"
                className="gap-2"
              >
                <LucidePlus size={14} />
                New Class
              </Button>
            }
          />
        ) : (
        <ClassList
          items={classes}
          getLinkPath={(item) => `/classes/${item._id}`}
          renderNameExtra={(item) =>
            item.reportsCount !== undefined ? (
              <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full shrink-0">
                {item.reportsCount}{" "}
                {item.reportsCount === 1 ? "report" : "reports"}
              </span>
            ) : null
          }
          getMenuItems={(item) => [
            { label: "Edit", icon: LucidePencil, onClick: () => setEditingClass(item) },
            { label: "Duplicate", icon: LucideCopy, onClick: () => setDuplicatingClass(item) },
            { label: "Delete", icon: LucideTrash, onClick: () => handleDelete(item), destructive: true },
          ]}
        />
        )}
      </div>

      <CreateClassModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleCreate}
        creating={creating}
      />

      <EditClassModal
        classItem={editingClass}
        onClose={() => setEditingClass(null)}
        onSubmit={handleUpdate}
        updating={updating}
      />

      <DuplicateClassModal
        classItem={duplicatingClass}
        onClose={() => setDuplicatingClass(null)}
        onSubmit={handleDuplicate}
        duplicating={duplicating}
      />
    </>
  );
}
